import type { Strokes } from '@clim/domain';
import { useEffect, useRef } from 'react';

/**
 * Zone de signature tactile / souris / stylet. Le tracé est enregistré en VECTEURS, normalisés
 * sur la largeur de la zone (0..10000) : indépendant de l'écran, rejoué à l'identique dans le PDF.
 */
export function SignaturePad({ value, onChange }: { value: Strokes; onChange(v: Strokes): void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<[number, number][] | null>(null);
  const strokes = useRef<Strokes>(value);
  strokes.current = value;

  const redraw = () => {
    const c = canvas.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const g = c.getContext('2d')!;
    g.scale(ratio, ratio);
    g.lineWidth = 2.2;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.strokeStyle = '#0d1a4d';
    const k = rect.width / 10_000;
    for (const s of [...strokes.current, ...(drawing.current ? [drawing.current] : [])]) {
      g.beginPath();
      s.forEach(([x, y], i) => (i ? g.lineTo(x * k, y * k) : g.moveTo(x * k, y * k)));
      if (s.length === 1) g.lineTo(s[0]![0] * k + 0.5, s[0]![1] * k + 0.5);
      g.stroke();
    }
  };
  useEffect(redraw);
  useEffect(() => {
    window.addEventListener('resize', redraw);
    return () => window.removeEventListener('resize', redraw);
  });

  const point = (e: React.PointerEvent): [number, number] => {
    const rect = canvas.current!.getBoundingClientRect();
    const clamp = (v: number) => Math.max(0, Math.min(10_000, Math.round(v)));
    return [clamp(((e.clientX - rect.left) / rect.width) * 10_000), clamp(((e.clientY - rect.top) / rect.width) * 10_000)];
  };

  return (
    <canvas
      ref={canvas}
      className="signature-pad"
      role="img"
      aria-label="Zone de signature"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = [point(e)];
        redraw();
      }}
      onPointerMove={(e) => {
        if (!drawing.current) return;
        const p = point(e);
        const last = drawing.current[drawing.current.length - 1]!;
        if (Math.abs(p[0] - last[0]) + Math.abs(p[1] - last[1]) < 15) return;
        drawing.current.push(p);
        redraw();
      }}
      onPointerUp={() => {
        if (drawing.current) onChange([...strokes.current, drawing.current]);
        drawing.current = null;
      }}
      onPointerCancel={() => {
        drawing.current = null;
        redraw();
      }}
    />
  );
}
